import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { PortalKey } from '@/features/access/portals';

type Screen = LazyExoticComponent<ComponentType>;

/**
 * Delivered portal screens, keyed by portal and nav path ('' = portal home).
 * Nav items without a screen render the module foundation page; Milestone 2
 * items never get a screen under the current contract (CLAUDE.md §3).
 */
export const PORTAL_SCREENS: Partial<Record<PortalKey, Record<string, Screen>>> = {
  customer: {
    '': lazy(() => import('@/pages/customer/CustomerOverviewPage')),
    vehicles: lazy(() => import('@/pages/customer/MyVehiclesPage')),
    bookings: lazy(() => import('@/pages/customer/MyBookingsPage')),
    quotes: lazy(() => import('@/pages/customer/MyCommercePages').then((m) => ({ default: m.MyQuotesPage }))),
    orders: lazy(() => import('@/pages/customer/MyCommercePages').then((m) => ({ default: m.MyOrdersPage }))),
    payments: lazy(() => import('@/pages/customer/MyCommercePages').then((m) => ({ default: m.MyPaymentsPage }))),
    warranty: lazy(() => import('@/pages/customer/MyWarrantyPage')),
    journey: lazy(() => import('@/pages/customer/MyVehicleJourneyPage')),
    profile: lazy(() => import('@/pages/customer/ProfilePage')),
  },
  executive: {
    '': lazy(() => import('@/pages/executive/ExecutiveDashboardPage')),
  },
  marketing: {
    '': lazy(() => import('@/pages/marketing/MarketingOverviewPage')),
    attribution: lazy(() => import('@/pages/marketing/AttributionPage')),
    sources: lazy(() => import('@/pages/marketing/TrafficSourcesPage')),
    campaigns: lazy(() => import('@/pages/marketing/CampaignsPage')),
    journeys: lazy(() => import('@/pages/marketing/JourneysPage')),
    leads: lazy(() => import('@/pages/marketing/LeadsPage')),
    connectors: lazy(() => import('@/pages/marketing/ConnectorsPage')),
    reports: lazy(() => import('@/pages/marketing/MarketingReportsPage')),
  },
  finance: {
    '': lazy(() => import('@/pages/finance/FinanceOverviewPage')),
    invoices: lazy(() => import('@/pages/finance/InvoicesPage')),
    receivables: lazy(() => import('@/pages/finance/ReceivablesPage')),
    collections: lazy(() => import('@/pages/finance/CollectionsPage')),
    payments: lazy(() => import('@/pages/finance/PaymentsPage')),
    reconciliation: lazy(() => import('@/pages/finance/ReconciliationPage')),
    terms: lazy(() => import('@/pages/finance/PaymentTermsPage')),
    reports: lazy(() => import('@/pages/finance/FinanceReportsPage')),
  },
  hr: {
    '': lazy(() => import('@/pages/people/hr/HrOverviewPage')),
    employees: lazy(() => import('@/pages/people/hr/EmployeesPage')),
    leave: lazy(() => import('@/pages/people/hr/LeavePage')),
    balances: lazy(() => import('@/pages/people/hr/BalancesPage')),
    entitlements: lazy(() => import('@/pages/people/hr/EntitlementsPage')),
    reports: lazy(() => import('@/pages/people/hr/HrReportsPage')),
  },
  employee: {
    '': lazy(() => import('@/pages/people/employee/EmployeeOverviewPage')),
    profile: lazy(() => import('@/pages/people/employee/MyProfilePage')),
    leave: lazy(() => import('@/pages/people/employee/MyLeavePage')),
    entitlements: lazy(() => import('@/pages/people/employee/MyEntitlementsPage')),
    'audit-actions': lazy(() => import('@/pages/people/employee/MyAuditActionsPage')),
    tickets: lazy(() => import('@/pages/people/employee/MyTicketsPage')),
    notifications: lazy(() => import('@/pages/people/employee/MyNotificationsPage')),
    mail: lazy(() => import('@/pages/people/employee/MailPage')),
    meetings: lazy(() => import('@/pages/people/employee/MeetingsPage')),
  },
  qa: {
    '': lazy(() => import('@/pages/quality/QualityOverviewPage')),
    checks: lazy(() => import('@/pages/quality/InspectionsPage')),
    complaints: lazy(() => import('@/pages/quality/ComplaintsPage')),
    'warranty-claims': lazy(() => import('@/pages/quality/WarrantyQualityPage')),
    satisfaction: lazy(() => import('@/pages/quality/SatisfactionPage')),
    defects: lazy(() => import('@/pages/quality/DefectsPage')),
    actions: lazy(() => import('@/pages/quality/ActionsPage')),
    reports: lazy(() => import('@/pages/quality/QualityReportsPage')),
  },
  workshop: {
    '': lazy(() => import('@/pages/workshop/WorkshopOverviewPage')),
    jobs: lazy(() => import('@/pages/workshop/JobsPage')),
    bookings: lazy(() => import('@/pages/workshop/AppointmentsPage')),
    warranties: lazy(() => import('@/pages/workshop/WarrantiesPage')),
    inventory: lazy(() => import('@/pages/inventory/StockPage')),
  },
  logistics: {
    '': lazy(() => import('@/pages/logistics/LogisticsOverviewPage')),
    shipments: lazy(() => import('@/pages/logistics/ShipmentsPage')),
    tracking: lazy(() => import('@/pages/logistics/TrackingViewPage')),
    deliveries: lazy(() => import('@/pages/logistics/DeliveriesPage')),
  },
  pos: {
    '': lazy(() => import('@/pages/pos/PosRegisterPage')),
    sales: lazy(() => import('@/pages/pos/PosSalesPage')),
  },
  inventory: {
    '': lazy(() => import('@/pages/inventory/InventoryOverviewPage')),
    stock: lazy(() => import('@/pages/inventory/StockPage')),
    rolls: lazy(() => import('@/pages/inventory/PackagesPage')),
    batches: lazy(() => import('@/pages/inventory/BatchesPage')),
    movements: lazy(() => import('@/pages/inventory/MovementsPage')),
    receiving: lazy(() => import('@/pages/inventory/ReceivingPage')),
    allocations: lazy(() => import('@/pages/inventory/ReservationsPage')),
    traceability: lazy(() => import('@/pages/inventory/TraceabilityPage')),
    alerts: lazy(() => import('@/pages/inventory/AlertsPage')),
    reports: lazy(() => import('@/pages/inventory/InventoryReportsPage')),
  },
  audit: {
    '': lazy(() => import('@/pages/audit/AuditOverviewPage')),
    trail: lazy(() => import('@/pages/admin/AuditLogPage')),
    activity: lazy(() => import('@/pages/audit/ActivityPage')),
    access: lazy(() => import('@/pages/admin/AccessLogPage')),
    approvals: lazy(() => import('@/pages/audit/ApprovalsPage')),
    evidence: lazy(() => import('@/pages/audit/EvidencePage')),
    engagements: lazy(() => import('@/pages/audit/CasesPage')),
    findings: lazy(() => import('@/pages/audit/FindingsPage')),
    'follow-up': lazy(() => import('@/pages/audit/FindingsPage')),
    reports: lazy(() => import('@/pages/audit/AuditReportsPage')),
  },
  admin: {
    '': lazy(() => import('@/pages/admin/ControlCenterPage')),
    users: lazy(() => import('@/pages/admin/UsersPage')),
    roles: lazy(() => import('@/pages/admin/RolesPage')),
    'access-log': lazy(() => import('@/pages/admin/AccessLogPage')),
    integrations: lazy(() => import('@/pages/admin/IntegrationsPage')),
    settings: lazy(() => import('@/pages/admin/SettingsPage')),
    audit: lazy(() => import('@/pages/admin/AuditLogPage')),
    notifications: lazy(() => import('@/pages/admin/NotificationsPage')),
    communications: lazy(() => import('@/pages/admin/CommunicationsPage')),
    'vehicle-assets': lazy(() => import('@/pages/admin/VehicleAssetCoveragePage')),
    catalog: lazy(() => import('@/pages/admin/SpecsReviewPage')),
    services: lazy(() => import('@/pages/admin/ServicePricingPage')),
    preview: lazy(() => import('@/pages/admin/PortalPreviewPage')),
    payments: lazy(() => import('@/pages/admin/PaymentsSetupPage')),
    identities: lazy(() => import('@/pages/people/identity/IdentitiesPage')),
    suppliers: lazy(() => import('@/pages/admin/SuppliersPage')),
    franchises: lazy(() => import('@/pages/admin/FranchisesPage')),
    legacy: lazy(() => import('@/pages/admin/LegacyDataPage')),
    support: lazy(() => import('@/pages/admin/SupportQueuePage')),
    assistant: lazy(() => import('@/pages/admin/KnowledgeBasePage')),
  },
  business: {
    '': lazy(() => import('@/pages/business/BusinessOverviewPage')),
    transactions: lazy(() => import('@/pages/business/BusinessTransactionsPage')),
    quality: lazy(() => import('@/pages/business/BusinessQualityPage')),
    complaints: lazy(() => import('@/pages/business/BusinessComplaintsPage')),
    warranties: lazy(() => import('@/pages/business/BusinessWarrantiesPage')),
    performance: lazy(() => import('@/pages/business/BusinessPerformancePage')),
  },
  vendor: {
    '': lazy(() => import('@/pages/vendor/VendorOverviewPage')),
    profile: lazy(() => import('@/pages/vendor/VendorProfilePage')),
    contacts: lazy(() => import('@/pages/vendor/VendorContactsPage')),
    documents: lazy(() => import('@/pages/vendor/VendorDocumentsPage')),
    messages: lazy(() => import('@/pages/vendor/VendorMessagesPage')),
    history: lazy(() => import('@/pages/vendor/VendorHistoryPage')),
  },
};
